<template>
  <div class="create-ad-container">
    <!-- 操作按钮区域 -->
    <div class="action-buttons">
      <el-button 
        type="primary" 
        size="small"
        icon="el-icon-download" 
        @click="downloadTemplate"
        :loading="downloadLoading"
      >
        下载模板
      </el-button>
      
      <el-upload
        ref="upload"
        :action="uploadUrl"
        :data="uploadData"
        :before-upload="beforeUpload"
        :on-success="handleUploadSuccess"
        :on-error="handleUploadError"
        :show-file-list="false"
        accept=".xlsx,.xls"
        style="display: inline-block; margin-left: 8px;"
      >
        <el-button 
          type="success" 
          size="small"
          icon="el-icon-upload2"
          :loading="uploadLoading"
        >
          {{ uploadButtonText }}
        </el-button>
      </el-upload>

      <el-button 
        type="info" 
        size="small"
        icon="el-icon-refresh" 
        @click="refreshStatus"
        :loading="refreshLoading"
        style="margin-left: 8px;"
      >
        刷新状态
      </el-button>

      <el-button 
        type="danger" 
        size="small"
        icon="el-icon-close" 
        @click="handleStopAll"
        :loading="stopAllLoading"
        style="margin-left: 8px;"
      >
        全部终止执行
      </el-button>
    </div>

    <!-- 数据表格 -->
    <div class="table-container">
      <el-table
        :data="tableData"
        v-loading="tableLoading"
        stripe
        border
        size="small"
        style="width: 100%"
        :default-sort="{ prop: 'importTime', order: 'descending' }"
      >
        <el-table-column
          prop="advertiserId"
          label="账户ID"
          width="150"
          align="center"
        />
        <el-table-column
          prop="campaignName"
          label="系列名称"
          width="200"
          show-overflow-tooltip
        />
        <el-table-column
          prop="bidType"
          label="出价策略"
          width="150"
          align="center"
        />
        <el-table-column
          prop="bidPrice"
          label="平均点击成本"
          width="130"
          align="center"
        />
        <el-table-column
          prop="conversionBidPrice"
          label="平均浏览成本"
          width="130"
          align="center"
        />
        <el-table-column
          prop="budget"
          label="预算"
          width="100"
          align="center"
        />
        <el-table-column
          prop="landingPageUrls"
          label="落地页链接"
          min-width="200"
          show-overflow-tooltip
        />
        <el-table-column
          prop="importTime"
          label="导入时间"
          width="150"
          align="center"
          sortable
        />
        <el-table-column
          prop="importStatus"
          label="导入状态"
          width="200"
          align="center"
        >
          <template slot-scope="scope">
            <div class="status-cell">
              <span 
                v-if="scope.row.importStatus === 1" 
                class="status-pending"
              >
                执行中...
              </span>
              <span 
                v-else-if="scope.row.importStatus === 2" 
                class="status-success"
              >
                导入成功
              </span>
              <span 
                v-else-if="scope.row.importStatus === 3" 
                class="status-error"
              >
                TT接口调用失败
              </span>
              <span 
                v-else-if="scope.row.importStatus === 4" 
                class="status-error"
              >
                调用TT接口入参错误
              </span>
              <span 
                v-else 
                class="status-unknown"
              >
                未知状态
              </span>
            </div>
          </template>
        </el-table-column>
        <el-table-column
          prop="importMessage"
          label="TT返回消息"
          min-width="200"
          show-overflow-tooltip
        />
        <el-table-column
          label="操作"
          width="120"
          align="center"
          fixed="right"
        >
          <template slot-scope="scope">
            <el-button
              type="danger"
              size="mini"
              icon="el-icon-close"
              v-if="scope.row.importStatus !== 2"
              :loading="scope.row.deleteLoading"
              @click="handleTerminate(scope.row)"
            >
              终止执行
            </el-button>
          </template>
        </el-table-column>
      </el-table>
    </div>

    <!-- 分页组件 -->
    <div class="pagination-container">
      <el-pagination
        @size-change="handleSizeChange"
        @current-change="handleCurrentChange"
        :current-page="currentPage"
        :page-sizes="[10, 20, 50, 100]"
        :page-size="pageSize"
        layout="total, sizes, prev, pager, next, jumper"
        :total="total"
      />
    </div>
  </div>
</template>

<script>
  import axios from 'axios'

  export default {
    name: 'CreateAd',
    data() {
      return {
        // 表格数据
        tableData: [],
        tableLoading: false,

        // 分页数据
        currentPage: 1,
        pageSize: 20,
        total: 0,

        // 上传相关
        uploadUrl: '/launch/buy/data/import/advertise', // 导入广告数据接口
        uploadData: {},
        uploadLoading: false,
        downloadLoading: false,
        refreshLoading: false,
        stopAllLoading: false,

        // 按钮文本
        uploadButtonText: '导入'
      }
    },

    mounted() {
      this.loadData()
    },

    methods: {
      // 加载表格数据
      async loadData() {
        this.tableLoading = true
        try {
          const response = await this.getAdvertiseList()
          if (response.header.code === 0) {
            this.tableData = response.body.data || []
            this.total = response.body.total || 0
          } else {
            this.$message.error('加载数据失败: ' + response.header.msg)
          }
        } catch (error) {
          this.$message.error('加载数据失败: ' + error.message)
        } finally {
          this.tableLoading = false
        }
      },

      // 获取广告数据列表
      async getAdvertiseList() {
        const params = {
          pageNum: this.currentPage,
          pageSize: this.pageSize
        }

        const response = await axios.get('/launch/buy/data/list/advertise', {
          params,
          headers: {
            'Authorization': `Bearer ${this.$store.state.user.token || ''}`
          }
        })

        return response.data
      },

      // 下载模板
      downloadTemplate() {
        this.downloadLoading = true
        try {
          // 创建下载链接
          const link = document.createElement('a')
          link.href = '/template.xlsx' // public目录下的模板文件
          link.download = '广告投放模板.xlsx'
          document.body.appendChild(link)
          link.click()
          document.body.removeChild(link)

          this.$message.success('模板下载成功')
        } catch (error) {
          this.$message.error('模板下载失败: ' + error.message)
        } finally {
          this.downloadLoading = false
        }
      },

      // 上传前验证
      beforeUpload(file) {
        const isExcel = file.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
          file.type === 'application/vnd.ms-excel'
        const isLt10M = file.size / 1024 / 1024 < 10

        if (!isExcel) {
          this.$message.error('只能上传 Excel 文件!')
          return false
        }
        if (!isLt10M) {
          this.$message.error('文件大小不能超过 10MB!')
          return false
        }

        this.uploadLoading = true
        return true
      },

      // 上传成功处理
      handleUploadSuccess(response, file) {
        this.uploadLoading = false

        if (response.header && response.header.code === 0) {
          this.$message.success('文件上传成功')
          this.loadData() // 重新加载数据
        } else {
          this.$message.error('上传失败: ' + (response.header?.msg || response.message || '未知错误'))
        }
      },

      // 上传失败处理
      handleUploadError(error) {
        this.uploadLoading = false
        this.$message.error('上传失败: ' + error.message)
      },

      // 刷新状态
      async refreshStatus() {
        this.refreshLoading = true
        try {
          await this.loadData() // 重新加载数据
          this.$message.success('状态刷新成功')
        } catch (error) {
          this.$message.error('状态刷新失败: ' + error.message)
        } finally {
          this.refreshLoading = false
        }
      },

      // 分页大小改变
      handleSizeChange(val) {
        this.pageSize = val
        this.currentPage = 1
        this.loadData()
      },

      // 当前页改变
      handleCurrentChange(val) {
        this.currentPage = val
        this.loadData()
      },

      // 终止执行
      async handleTerminate(row) {
        try {
          await this.$confirm(
            `确定要终止系列"${row.campaignName}"的执行吗？`,
            '终止执行',
            {
              confirmButtonText: '确定',
              cancelButtonText: '取消',
              type: 'warning'
            }
          )

          // 设置loading状态
          this.$set(row, 'deleteLoading', true)

          // 调用删除API
          const response = await this.removeAdvertise(row.campaignName)

          if (response.header && response.header.code === 0) {
            this.$message.success('终止执行成功')
            this.loadData() // 重新加载数据
          } else {
            this.$message.error('终止执行失败: ' + (response.header?.msg || '未知错误'))
          }
        } catch (error) {
          if (error !== 'cancel') {
            this.$message.error('终止执行失败: ' + error.message)
          }
        } finally {
          // 清除loading状态
          this.$set(row, 'deleteLoading', false)
        }
      },

      // 删除广告数据
      async removeAdvertise(campaignName) {
        const response = await axios.post('/launch/buy/data/remove/advertise', null, {
          params: {
            campaignName: campaignName
          },
          headers: {
            'Authorization': `Bearer ${this.$store.state.user.token || ''}`
          }
        })

        return response.data
      },

      // 全部终止执行
      async handleStopAll() {
        try {
          this.stopAllLoading = true

          // 调用全部终止API
          const response = await this.stopAllAdvertise()

          if (response.header && response.header.code === 0) {
            this.$message.success('全部终止执行成功')
            this.loadData() // 重新加载数据
          } else {
            this.$message.error('全部终止执行失败: ' + (response.header?.msg || '未知错误'))
          }
        } catch (error) {
          if (error !== 'cancel') {
            this.$message.error('全部终止执行失败: ' + error.message)
          }
        } finally {
          this.stopAllLoading = false
        }
      },

      // 全部终止广告数据
      async stopAllAdvertise() {
        const response = await axios.post('/launch/buy/data/stop/all', null, {
          headers: {
            'Authorization': `Bearer ${this.$store.state.user.token || ''}`
          }
        })

        return response.data
      }
    }
  }
</script>

<style scoped>
  .create-ad-container {
    padding: 20px;
    background: #fff;
    min-height: calc(100vh - 60px);
  }

  .page-header {
    margin-bottom: 20px;
    padding-bottom: 15px;
    border-bottom: 1px solid #e4e7ed;
  }

  .page-header h2 {
    margin: 0 0 8px 0;
    color: #303133;
    font-size: 24px;
    font-weight: 500;
  }

  .page-description {
    margin: 0;
    color: #909399;
    font-size: 14px;
  }

  .action-buttons {
    margin-bottom: 20px;
    display: flex;
    align-items: center;
    justify-content: flex-end;
  }

  .table-container {
    margin-bottom: 20px;
  }

  .pagination-container {
    display: flex;
    justify-content: flex-end;
    margin-top: 20px;
  }

  .status-cell {
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .status-success {
    color: #67c23a;
    font-weight: 500;
  }

  .status-error {
    color: #f56c6c;
    font-weight: 500;
  }

  .status-pending {
    color: #e6a23c;
    font-weight: 500;
  }

  .status-unknown {
    color: #909399;
    font-weight: 500;
  }

  /* 响应式设计 */
  @media (max-width: 768px) {
    .create-ad-container {
      padding: 15px;
    }

    .action-buttons {
      flex-direction: column;
      align-items: stretch;
      justify-content: flex-start;
    }

    .action-buttons .el-button {
      margin: 5px 0;
    }

    .action-buttons .el-upload {
      margin: 5px 0;
    }

    .pagination-container {
      justify-content: center;
    }
  }
</style>
